import { Validation } from 'components/Utils/Validation';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useUpdate } from 'react-use';
import {
  VerifiableContactDetailField,
  VerifiableContactDetailFieldBaseProps,
  VerifiableContactDetailFieldButtonProps,
  VerifiableContactDetailFieldInputProps,
  VerifiableContactDetailFieldProps,
} from './components/VerifiableContactDetailField';
import type { VerifiableContactDetail, ValidatedVerifiableAustralianMobile } from './types';

type ControlledProps = {
  verifiedMobileNumbers?: VerifiableContactDetailFieldProps['verifiedContactDetails'];
  defaultVerifiedMobileNumbers?: never;
};

type UncontrolledProps = {
  verifiedMobileNumbers?: never;
  defaultVerifiedMobileNumbers?: VerifiableContactDetailFieldProps['defaultVerifiedContactDetails'];
};

export type VerifiableAustralianMobileFieldProps = (ControlledProps | UncontrolledProps) &
  VerifiableContactDetailFieldBaseProps & {
    inputProps?: Omit<VerifiableContactDetailFieldProps['inputProps'], 'label'> & {
      label?: string;
    };

    buttonProps?: Omit<VerifiableContactDetailFieldProps['buttonProps'], 'label'> & {
      label?: string;
    };

    onChange: (next: ValidatedVerifiableAustralianMobile) => void;
    validateMobileNumber?: (next?: string) => boolean;

    onVerifiedMobileNumbersChange?: VerifiableContactDetailFieldProps['onVerifiedContactDetailsChange'];
  };

const DEFAULT_INPUT_PROPS: VerifiableContactDetailFieldInputProps = {
  label: 'Mobile number',
  inputWidth: 'md',
};

const DEFAULT_BUTTON_PROPS: VerifiableContactDetailFieldButtonProps = {
  label: 'Verify',
};

const replacePrefix = (mobileNumber: string) => mobileNumber?.replace(/^(?:\+?61)\s*/, '0') ?? '';
const normalizeMobileNumber = (mobileNumber: string) => replacePrefix(mobileNumber).replace(/\D/g, '');

const buildLocalAndInternationalNumbers = (
  value: string
): Pick<ValidatedVerifiableAustralianMobile, 'value' | 'localValue' | 'internationalValue'> => {
  const unprefixed = replacePrefix(value);

  return {
    value,
    localValue: unprefixed,
    internationalValue: unprefixed,
  };
};

export const VerifiableAustralianMobileField = forwardRef<HTMLInputElement, VerifiableAustralianMobileFieldProps>(
  (props, ref) => {
    const {
      value,
      inputProps: overrideInputProps,
      buttonProps: overrideButtonProps,
      onChange,
      validateMobileNumber = Validation.isAustralianMobileNumber,
      verifiedMobileNumbers,
      defaultVerifiedMobileNumbers = [],
      onVerifiedMobileNumbersChange,
      ...restProps
    } = props;

    const inputRef = useRef<HTMLInputElement>();
    useImperativeHandle(ref, () => inputRef.current);

    const forceUpdate = useUpdate();
    const isValid = validateMobileNumber(value);
    const inputHasFocus = () => document.activeElement === inputRef.current;

    const inputProps = { ...DEFAULT_INPUT_PROPS, ...(overrideInputProps ?? {}) };
    const buttonProps = { ...DEFAULT_BUTTON_PROPS, ...(overrideButtonProps ?? {}) };

    const reformatDisplayValue = (value: string) => {
      if (inputHasFocus()) return value;
      return inputProps.prefix ? replacePrefix(value?.trim()) : value?.trim();
    };

    const getDisplayValue = () => {
      // If the input is not currently focused, then we always use the reformatted display value (which accounts for
      // the prefix on the field).
      const reformatted = reformatDisplayValue(value);
      if (!inputHasFocus()) return reformatted;

      // If the input currently has focused, then use whatever the current "display" value has been passed in. However,
      // if the value passed in is actually equivalent to the current display/local/international value of whatever the
      // control is showing, then don't modify the value shown in the control.
      const { localValue, internationalValue } = buildLocalAndInternationalNumbers(reformatted);
      return [value, localValue, internationalValue].map(normalizeMobileNumber).includes(normalizeMobileNumber(value))
        ? inputRef.current.value
        : value;
    };

    const displayValue = getDisplayValue();

    const handleChange = ({ value, ...rest }: VerifiableContactDetail) => {
      const reformattedDisplayValue = reformatDisplayValue(value);

      onChange({
        ...rest,
        ...buildLocalAndInternationalNumbers(reformattedDisplayValue),
        invalid: !validateMobileNumber(value),
      });

      // This is to cater for the edge cases such as:
      //
      //   - The control has a prefix of "+61"
      //   - The displayed value is "444555666"
      //   - The parent control is supplying the "local value" to this control - ie. `value={0444555666}`
      //
      // In this example, if the user tries to enter a "0" at the start of the input field, this 'onChange' handler
      // will fire with a value of "0444555666". The parent control will update it's local state to use this new
      // value, but React will reject the change (since the string hasn't actually updated). As such, this control
      // will not re-render, which means the input field will keep displaying its original value of "444555666".
      //
      // To cater for this, we force a rerender of the control. Since the input field still has focus, we won't try
      // and reformat the value, so it will show the value as the user has entered it. Forcing a rerender is mostly
      // harmless since updates are batched - that is, if the control was _already_ going to rerender (because of a
      // props change), the forced rerender here will basically get ignored.
      if (reformattedDisplayValue !== displayValue) {
        forceUpdate();
      }
    };

    return (
      <VerifiableContactDetailField
        ref={inputRef}
        type="mobile"
        value={getDisplayValue()}
        inputProps={inputProps}
        buttonProps={buttonProps}
        enableVerify={isValid}
        onChange={handleChange}
        onBlur={handleChange}
        normalize={normalizeMobileNumber}
        verifiedContactDetails={verifiedMobileNumbers}
        defaultVerifiedContactDetails={defaultVerifiedMobileNumbers}
        onVerifiedContactDetailsChange={onVerifiedMobileNumbersChange}
        {...restProps}
      />
    );
  }
);
